import { ArrowUp, Square } from 'lucide-react';
import React, { useCallback, useRef } from 'react';

import { Button } from '@/components/ui/button';

const STARTER_PROMPTS = [
  'Show me my recent cases',
  'How many evaluations were done this week?',
  'Summarize the latest call for a case',
];

interface ChatInputProps {
  input: string;
  onInputChange: (value: string) => void;
  onSubmit: () => void;
  onStop: () => void;
  isLoading: boolean;
  suggestedPrompts?: string[];
}

export function ChatInput({
  input,
  onInputChange,
  onSubmit,
  onStop,
  isLoading,
  suggestedPrompts,
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const prompts = suggestedPrompts?.length ? suggestedPrompts : STARTER_PROMPTS;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (input.trim() && !isLoading) {
          onSubmit();
        }
      }
    },
    [input, isLoading, onSubmit],
  );

  const handlePromptClick = useCallback(
    (prompt: string) => {
      onInputChange(prompt);
      // Submit on next tick so input state has updated
      setTimeout(() => {
        onSubmit();
      }, 0);
    },
    [onInputChange, onSubmit],
  );

  return (
    <div className="border-t bg-background p-4">
      {!isLoading && !input.trim() && (
        <div className="mb-3 flex flex-wrap gap-2">
          {prompts.map(prompt => (
            <button
              key={prompt}
              type="button"
              onClick={() => handlePromptClick(prompt)}
              className="rounded-full border bg-background px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              {prompt}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={input}
          onChange={e => onInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about cases, evaluations, or calls..."
          rows={1}
          className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        {isLoading ? (
          <Button size="icon" variant="destructive" onClick={onStop} className="shrink-0">
            <Square className="h-4 w-4" />
          </Button>
        ) : (
          <Button size="icon" onClick={onSubmit} disabled={!input.trim()} className="shrink-0">
            <ArrowUp className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
